import { HttpError } from "./api.types";
import { getMockedResponse } from "./utils";

export interface CustomPromise<TSuccess, TError> extends Omit<Promise<TSuccess>, 'catch' | 'then'> {
  then<TResult1 = TSuccess, TResult2 = never>(
    onfulfilled?: ((value: TSuccess) => TResult1 | PromiseLike<TResult1>) | null,
    onrejected?: ((reason: TError) => TResult2 | PromiseLike<TResult2>) | null
  ): CustomPromise<TResult1 | TResult2, TError>;
  
  catch<TResult = never>(
    onrejected?: ((reason: TError) => TResult | PromiseLike<TResult>) | null
  ): CustomPromise<TSuccess | TResult, TError>;
}

export type ApiResponse<T = any> = {
    success: boolean; 
    response?: Response | null;
    jsonBody?: T | null;
    textBody?: string | null,
    blobBody?: Blob | null;
    error?: any, 
    retry?: CustomPromise<ApiResponse<T>, ApiResponse<T>> | null
}


const RETRY_TIMES = 3;
const RETRY_WAIT = 100; // ms

export type MockDataForRequestManager = {
    data: any;
    status: number;
    error?: {
        message: string;
        code?: string;
    }
};

export type MockDataForRequestManagerMethod = () => MockDataForRequestManager;

export type FetchConfig<T = any> = {
    mock?: boolean;
    mockData?: MockDataForRequestManager | MockDataForRequestManagerMethod;
    getMockStatus?: () => number | undefined;
    transformResponsePayload?: (apiResponse: ApiResponse) => Promise<ApiResponse<T>>;
} 

type FetchRequestInfo = {
    input: RequestInfo | URL;
    init?: RequestInit;
    resolve: any;
    reject: any;
    attemptsLeft: number;
} & FetchConfig;

export class RequestManager {
    private _authToken: string | null = null;
    private _requestQueueAuthorized: FetchRequestInfo[] = [];
    private _requestQueueUnauthorized: FetchRequestInfo[] = [];

    private _processQueue = (type: 'authorized' | 'unauthorized') => {
        if ((type === 'authorized' && (!this._requestQueueAuthorized.length || !this._authToken)) 
            || (type === 'unauthorized' && !this._requestQueueUnauthorized.length)) {
            return;
        }
        const fetchRequestInfo = type === 'authorized' ? this._requestQueueAuthorized.shift()! : this._requestQueueUnauthorized.shift()!;
        let { input, init, attemptsLeft, mock, mockData, getMockStatus, transformResponsePayload } = fetchRequestInfo;
        if (mock && typeof mockData === 'function') {
            mockData = mockData();
        }
        const headers: Record<string, any> = {
            ...(init?.headers || {})
        };
        if (type === 'authorized') {
            headers['Authorization'] = `Bearer ${this._authToken}`;
        }
        let request: Promise<Response> | null = null;
        if (mock) {
            const mockStatus = getMockStatus ? getMockStatus() : undefined;
            const { data, status, error } = (mock ? (mockData || {}) : {}) as MockDataForRequestManager;
            request = new Promise((resolve, reject) => {
                // Prefer the mockStatus provided by testing
                const checkStatus = mockStatus || status || 200;
                const mockResp = getMockedResponse(data, checkStatus, error);
                if (checkStatus >= 200 && checkStatus < 300) {
                    resolve(mockResp);
                } else {
                    reject(mockResp)
                }
            })
        } else {
            request = fetch(
                input,
                {
                    ...(init || {}),
                    headers
                }
            );
        }
        request!.then(async (response) => {
            if (!response.ok) {
                let serverMessage = '';
                try {
                    const body = await response.clone().json();
                    const detail = body?.detail || body?.error;
                    serverMessage = typeof detail === 'string' ? detail : '';
                } catch (e) {
                    // Body is empty or not JSON
                }
                throw new HttpError(serverMessage || 'Uncaught server exception', response.status);
            }
            let respData: ApiResponse = {
                success: true,
                response,
                error: null,
                retry: null
            };
            if (transformResponsePayload) {
                respData = await transformResponsePayload(respData);
            }
            fetchRequestInfo.resolve(respData);
        }).catch(error => {
            const canRetry = error.status === 503 && attemptsLeft >= 1;
            if (canRetry) {
                console.log('503 encountered. Allowing retry', (typeof input === 'string' && input) || input.toString());
            }
            const retry = canRetry ? new Promise((resolve, reject) => {
                console.log('Retry invoked after encountering 503', (typeof input === 'string' && input) || input.toString());
                setTimeout(() => {
                    if (type === 'authorized') {
                        this._appendToAuthorizedQueue({
                            ...fetchRequestInfo,
                            resolve, reject,
                            attemptsLeft: attemptsLeft-1 
                        });
                    } else {
                        this._appendToUnauthorizedQueue({
                            ...fetchRequestInfo,
                            resolve, reject,
                            attemptsLeft: attemptsLeft-1
                        });
                    }
                }, RETRY_WAIT);
            }) : null;
            fetchRequestInfo.reject({
                success: false,
                response: null,
                error,
                retry: retry
            });
        });
        this._processQueue(type);
    }

    private _appendToAuthorizedQueue(info: FetchRequestInfo) {
        this._requestQueueAuthorized.push(info);
        this._processQueue('authorized');
    }

    private _appendToUnauthorizedQueue(info: FetchRequestInfo) {
        this._requestQueueUnauthorized.push(info);
        this._processQueue('unauthorized');
    }

    setAuthToken(token: string | null) {
        this._authToken = token;
        this._processQueue('authorized');
    }

    standardAuthRequest<T = any>(input: RequestInfo | URL, init?: RequestInit, config?: FetchConfig<T>): CustomPromise<ApiResponse<T>, ApiResponse<T>> {
        return new Promise((resolve, reject) => {
            let attemptsLeft = 0;
            if (!init || init.method === 'GET') {
                attemptsLeft = RETRY_TIMES;
            }
            this._appendToAuthorizedQueue({ input, init, resolve, reject, attemptsLeft, ...(config || {}) })
        });
    }

    unauthorizedRequest<T = any>(input: RequestInfo | URL, init?: RequestInit, config?: FetchConfig<T>): CustomPromise<ApiResponse<T>, ApiResponse<T>> {
        return new Promise((resolve, reject) => {
            let attemptsLeft = 0;
            if (!init || init.method === 'GET') {
                attemptsLeft = RETRY_TIMES;
            }
            this._appendToUnauthorizedQueue({ input, init, resolve, reject, attemptsLeft, ...(config || {}) })
        });
    }
}
